import React, { FunctionComponent } from 'react'
import { FormattedMessage, FormattedTime } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { HoverActionContainer } from '../../organisms/hoverActionContainer/HoverActionContainer'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'
import { HoverActionItem } from '../hoverActions/HoverActions'

export type ProviderAgendaItemProps = {
  id: number
  appointmentDate: Date
  clientName: React.ReactNode
  appointmentInfo: React.ReactNode
  hoverActions: HoverActionItem[]
  onPress?: () => void
  completedOnboardingForms?: boolean
  badges?: React.ReactNode | React.ReactNode[]
  isLastItem?: boolean
  isLateCancellation?: boolean
}

const Container = styled(HoverActionContainer)(({ theme }) => ({
  paddingHorizontal: theme.spacing['16px'],
}))

const ContentRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  paddingVertical: theme.spacing['16px'],
  gap: theme.spacing['16px'],
}))

const RightColumn = styled.View({
  flex: 1,
  flexDirection: 'column',
  alignItems: 'flex-start',
})

const ClientInfoContainer = styled.View(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing['4px'],
  width: '100%',
}))

const ClientNameRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const ClientName = styled(BodyText)<{ $isLateCancellation: boolean }>(({ $isLateCancellation }) => ({
  textDecorationLine: $isLateCancellation ? 'line-through' : undefined,
}))

export const ProviderAgendaItem: FunctionComponent<ProviderAgendaItemProps> = ({
  id: _id,
  appointmentDate,
  clientName,
  appointmentInfo,
  hoverActions,
  onPress,
  completedOnboardingForms = true,
  badges,
  isLastItem = false,
  isLateCancellation = false,
}) => {
  const { colors } = useTheme()

  return (
    <Container items={hoverActions} onPress={onPress} testID={tID('ProviderAgendaItem')}>
      <ContentRow>
        <BodyText
          testID={tID('ProviderAgendaItem-time')}
          text={<FormattedTime value={appointmentDate} hour='numeric' minute='2-digit' hour12={true} />}
        />
        <RightColumn>
          <ClientInfoContainer>
            <ClientNameRow>
              <ClientName
                text={clientName}
                color={isLateCancellation ? colors.textWarning : undefined}
                $isLateCancellation={isLateCancellation}
              />
              {badges}
            </ClientNameRow>
            {!isLateCancellation && !completedOnboardingForms && (
              <BodyText
                size={BodyTextSize.SMALL}
                color={colors.textWarning}
                text={
                  <FormattedMessage
                    defaultMessage="Hasn't completed Lyra's onboarding form"
                    description='Warning shown on a scheduled appointment when the client has not finished onboarding'
                  />
                }
              />
            )}
            <BodyText
              size={BodyTextSize.CAPTION}
              color={isLateCancellation ? colors.textWarning : colors.textSecondary}
              medium={isLateCancellation}
              text={
                isLateCancellation ? (
                  <FormattedMessage
                    defaultMessage='Late cancellation'
                    description='Status of a late-cancelled appointment in the provider agenda'
                  />
                ) : (
                  appointmentInfo
                )
              }
            />
          </ClientInfoContainer>
        </RightColumn>
      </ContentRow>
      {!isLastItem && <Divider />}
    </Container>
  )
}
